import { useCallback, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';

import { Button, Card, Page, Inline, Input, Stack, Text, u, Heading2, Heading3, Spinner } from '@ui';

import { useResendConfirmationEmail, useUserConfirmEmail } from '../../hooks';

import * as css from './UserEmailConfirmationScreen.css';

type FormValues = {
    token: string;
    email: string;
};

export const UserEmailConfirmationScreen = () => {
    const { register, handleSubmit } = useForm<FormValues>();
    const [isResending, setIsResending] = useState(false);

    const [params] = useSearchParams();
    const email = params.get('email') || '';

    const confirmEmail = useUserConfirmEmail();
    const { mutateAsync: resendConfirmation, isSuccess } = useResendConfirmationEmail();
    const handleConfirmEmail = async (values: FormValues) => confirmEmail.mutate(values);

    // This will make the user see that the resend button is working it will spin for a second
    // And then it will change to disabled and notifying the user that the email has been sent
    const handleResend = useCallback(async () => {
        setIsResending(true);

        const start = Date.now();

        try {
            await resendConfirmation(email);
        } finally {
            const elapsed = Date.now() - start;
            const minDuration = 1_000;

            if (elapsed < minDuration) {
                await new Promise((res) => setTimeout(res, minDuration - elapsed));
            }

            setIsResending(false);
        }
    }, [email, resendConfirmation]);

    const navigate = useNavigate();

    return (
        <Page>
            <Card>
                <Stack gap={u(2.5)} style={{ width: '350px' }}>
                    <Heading2>Check Your Email</Heading2>
                    <Text>
                        A confirmation code has been sent to <strong>{email}</strong>.
                    </Text>
                    <p>In order to verify your email address, please enter the code below.</p>
                    <form onSubmit={handleSubmit(handleConfirmEmail)}>
                        <Stack gap={u(2.5)} style={{ width: '350px' }}>
                            {confirmEmail.error && (
                                <span className={css.errorMessage}>{confirmEmail.error.message}</span>
                            )}
                            <input type="hidden" value={email} {...register('email')} />
                            <Input
                                {...register('token')}
                                required
                                type="text"
                                label="Verification code"
                                placeholder="000000"
                            />
                            <Inline justifyContent="space-between">
                                <Button type="button" variant="secondary" onClick={() => navigate('/')}>
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    isLoading={confirmEmail.isPending}
                                    isDisabled={confirmEmail.isPending}
                                    variant="primary"
                                >
                                    Confirm
                                </Button>
                            </Inline>
                        </Stack>
                    </form>
                    <Stack py={u(2.5)} px={u(2)} mt={u(2)} className={css.notice}>
                        <Heading3>Didn’t receive the confirmation email?</Heading3>

                        <Text variant="p">Try again or reset your password to continue.</Text>

                        <Inline justifyContent="space-between">
                            <button
                                disabled={isResending || isSuccess}
                                onClick={handleResend}
                                className={css.primaryGhost}
                            >
                                {isResending ? (
                                    <span className={css.center}>
                                        <Spinner size="s" />
                                    </span>
                                ) : null}
                                <span
                                    style={{
                                        opacity: isResending ? 0 : 1,
                                        textDecoration: isSuccess ? 'underline' : 'unset',
                                    }}
                                >
                                    {isSuccess && !isResending ? 'EMAIL SENT' : 'RESEND EMAIL'}
                                </span>
                            </button>

                            <button onClick={() => navigate(`/reset-password?email=${email}`)} className={css.ghost}>
                                RESET PASSWORD
                            </button>
                        </Inline>
                    </Stack>
                </Stack>
            </Card>
        </Page>
    );
};
